import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import { groupTripConversationApi } from '../../api/groupTripConversationApi';
import { resolveApiErrorMessage } from '@formosa/shared/i18n/resolveApiErrorMessage';

/**
 * Opens GroupTrip BUSINESS_GROUP chat via Travel ensure → existing /messages/:id UI.
 */
export default function GroupTripChatButton({ groupTripId, className }) {
  const { t } = useTravelTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      generation.current += 1;
    };
  }, [groupTripId]);

  async function onClick() {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: authReturnLocation(location) } });
      return;
    }
    if (busy || groupTripId == null) return;
    const requestGeneration = ++generation.current;
    setBusy(true);
    setError('');
    try {
      let binding;
      try {
        binding = await groupTripConversationApi.get(groupTripId);
      } catch (cause) {
        if (cause?.status !== 404 && cause?.raw?.key !== 'travel.grouptrip.conversation.not.found') {
          throw cause;
        }
        binding = await groupTripConversationApi.ensure(groupTripId);
      }
      if (!alive.current || requestGeneration !== generation.current) return;
      const conversationId = binding?.conversationId;
      if (conversationId == null) {
        setError(t('travel.groupTrip.chatUnavailable'));
        return;
      }
      navigate(`/messages/${conversationId}`);
    } catch (cause) {
      if (!alive.current || requestGeneration !== generation.current) return;
      setError(resolveApiErrorMessage(cause, t, 'travel.groupTrip.chatOpenFailed'));
    } finally {
      if (requestGeneration === generation.current) setBusy(false);
    }
  }

  return (
    <span className="group-trip-chat">
      <button type="button" className={className} disabled={busy} onClick={onClick} aria-busy={busy}>
        {busy ? t('travel.groupTrip.chatOpening') : t('travel.groupTrip.groupChat')}
      </button>
      {error ? <p className="group-trips__error" role="alert">{error}</p> : null}
    </span>
  );
}
